import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';

// ── "Bill this month" for ONE client (Contact row) ───────────────────────────
// The monthly run bills every MONTHLY accounting client on the 25th. A client added after that run has
// no invoice, and re-running the whole month is not an option — that button REBUILDS every draft from
// current services, discarding any hand edits on the other clients' invoices.
//
// So this bills exactly one client, through the SAME generator the monthly run uses
// (generateInvoiceForClient → svcToItems), which matters because Social Security and Income Tax
// (PND.1) are NOT stored prices: they are computed at generation time from the client's current
// employees and split into one line per distinct amount. A hand-made invoice cannot reproduce them.
//
// Because those amounts are invisible until the moment of generation, the dialog PREVIEWS first
// (?dry=1 — computes and returns the lines, writes nothing, burns no document number) and only writes
// when the operator confirms. Preview and commit run the same code, so the approved figures are the
// stored ones.


export default function BillMonthDialog({ clientId, clientName, onClose, onDone }: {
  clientId: string; clientName: string; onClose: () => void; onDone?: () => void;
}) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<any>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Translate the generator's own reasons; anything else is a real error and is shown verbatim.
  const explain = (reason: string) =>
    reason === 'already invoiced this month' ? t('ct_bill_exists')
      : reason === 'no accounting services' ? t('ct_bill_nosvc')
      : reason;

  useEffect(() => {
    let live = true;
    api.generateInvoiceForClient(clientId, true)
      .then((r: any) => {
        if (!live) return;
        if (r.preview) { setPreview(r.preview); return; }
        setErr(explain(r.reason || 'Could not prepare this invoice.'));
      })
      .catch((e: any) => { if (live) setErr(e?.message || 'Failed'); });
    return () => { live = false; };
  }, [clientId, t]);   // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true); setErr('');
    try {
      // Commit the period the operator actually approved. Without this the server re-derives the month
      // from the clock, so a dialog left open across Bangkok midnight on the last day of a month would
      // silently bill the NEXT month instead of the one shown above.
      const r: any = await api.generateInvoiceForClient(clientId, false, preview?.period);
      onDone?.();
      onClose();
      alert(t('ct_bill_done').replace('{n}', r.docNo || r.invoiceId || ''));
    } catch (e: any) { setErr(explain(e?.message || 'Failed')); setBusy(false); }
  };

  // 11.5 → 12.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
  // Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like
  // this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular
  // weight, where they were dark grey in bold, 10px above and below where it was 7, on one 16px line, on white, over the
  // darker --border-strong line — a 37px band, as every list's is.
  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 10px', fontSize: 12.5, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const num: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
  const meta = (k: string, v: string) => (
    <div><span style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}>{k}</span><div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div></div>
  );
  const totalRow = (label: string, value: string, strong = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 30, padding: '4px 0', fontSize: strong ? 15 : 13, fontWeight: strong ? 800 : 500 }}>
      <span style={{ color: strong ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{label}</span><span>{value}</span>
    </div>
  );

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={{ background: 'var(--bg-panel)', borderRadius: 'var(--radius-lg)', width: 'min(760px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{t('ct_bill_title')}</div>
          {/* preview.clientName is the CLIENT record's company name — the name that will be printed on
              the invoice. The Contact row's label can differ (a contact person, an old trading name). */}
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{preview?.clientName || clientName}</div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {!preview && !err && <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px 0', fontSize: 13 }}>…</div>}
          {err && <div style={{ background: '#FCEBEB', color: '#B42318', padding: '12px 14px', borderRadius: 'var(--radius-md)', fontSize: 13 }}>{err}</div>}

          {preview && (
            <>
              <div style={{ display: 'flex', gap: 26, marginBottom: 14 }}>
                {meta(t('ct_bill_period'), `${preview.periodFrom} → ${preview.periodTo}`)}
                {meta(t('ct_bill_issue'), preview.issueDate)}
                {meta(t('ct_bill_due'), preview.dueDate)}
              </div>
              <div style={{ background: 'var(--accent-bg)', color: 'var(--accent-text)', padding: '10px 12px', borderRadius: 'var(--radius-md)', fontSize: 12.5, marginBottom: 14 }}>
                <i className="ti ti-info-circle" style={{ marginRight: 6 }} />{t('ct_bill_note')}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>&nbsp;</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('ct_bill_qty')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('ct_bill_price')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('ct_bill_amount')}</th>
                </tr></thead>
                <tbody>
                  {(preview.items || []).map((it: any, i: number) => (
                    <tr key={i}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{it.name}</div>
                        {/* The per-employee SSO / PND.1 breakdown lives in the description as plain text
                            with newlines — show it verbatim, it IS the justification for the amount. */}
                        {it.description && <div style={{ color: 'var(--text-secondary)', fontSize: 11.5, whiteSpace: 'pre-wrap', marginTop: 3 }}>{it.description}</div>}
                      </td>
                      <td style={num}>{it.quantity}</td>
                      <td style={num}>{money(it.unit_price)}</td>
                      <td style={{ ...num, fontWeight: 600 }}>{money(it.pre_vat_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 16, marginLeft: 'auto', width: 'min(320px, 100%)' }}>
                {totalRow(t('ct_bill_subtotal'), money((preview.totals?.total_amount || 0) - (preview.totals?.vat_amount || 0)))}
                {totalRow(t('ct_bill_vat'), money(preview.totals?.vat_amount))}
                {totalRow(t('ct_bill_wht'), `− ${money(preview.totals?.wht_amount)}`)}
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 4 }}>
                  {totalRow(t('ct_bill_net'), money(preview.totals?.net_to_pay), true)}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('cancel')}</button>
          {preview && (
            // Spinner + disabled on click: creating an invoice twice would burn a document number and
            // leave a duplicate that has to be voided (firm-wide rule for every slow button).
            <button onClick={create} disabled={busy}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: busy ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
              {busy && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 0.9s linear infinite' }} />}
              {busy ? t('ct_bill_creating') : t('ct_bill_create')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
